import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface Props {
  children: ReactNode;
  className?: string;
}

/**
 * Trailing action buttons of a table row (edit pencil, deactivate, impersonate,
 * …).
 *
 * The icons repeat on every row and read as clutter at rest, so they only paint
 * while the pointer is over the row or something inside them holds keyboard
 * focus. They stay in the layout (`opacity`, not `hidden`) so revealing one
 * never shifts the row, and they stay in the accessibility tree so screen
 * readers and Tab still reach them.
 *
 * The reveal keys off `tr:hover` via an arbitrary variant rather than
 * `group-hover`: most list tables hand-roll their `<TableRow>`s, and the one
 * place a `group` class could live for all of them — `components/ui/table.tsx`
 * — is shadcn-managed (G9). This keeps the trick in a single wrapper instead of
 * a `group` class sprinkled across fifteen row renderers. `tr:hover .actions`
 * (0,2,1) outranks the plain `.actions` hide (0,1,0), so source order between
 * the two utilities doesn't matter.
 *
 * The hide itself sits behind `@media (hover: hover)`: on touch screens nothing
 * ever hovers, so there the actions are simply always visible.
 */
export function RowActions({ children, className }: Props) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-1 transition-opacity',
        'focus-within:opacity-100 [@media(hover:hover)]:opacity-0 [tr:hover_&]:opacity-100',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Empty slot the exact size of an `icon-sm` button.
 *
 * The action set varies per row (only some employees can be impersonated, only
 * active records can be deactivated), and a right-aligned group with a missing
 * button drags every other icon in that row sideways — the column reads as a
 * zig-zag. Render this in place of an action a row doesn't get so the icons
 * stay in fixed vertical lanes.
 */
export function RowActionSpacer() {
  return <span aria-hidden className="size-8 shrink-0" />;
}
